"use client";

import React, { useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import type { Platform } from "@/payload-types";
import { cn } from "@/lib/utils";
import { Plus, X } from "lucide-react";

interface PlatformCarouselAccordionProps {
  platform: Platform;
}

export function PlatformCarouselAccordion({
  platform,
}: PlatformCarouselAccordionProps) {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(
    () => new Set([0])
  );
  const [prevPlatformId, setPrevPlatformId] = useState(platform.id);

  if (platform.id !== prevPlatformId) {
    setPrevPlatformId(platform.id);
    setExpandedItems(new Set([0]));
  }

  const toggleItem = (index: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const sections =
    platform.accordionItems?.map((item) => ({
      title: item.title,
      excerpt: item.excerpt,
    })) ?? [];

  if (sections.length === 0) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={platform.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="h-full p-6 lg:p-8"
      >
        <div className="border-t border-white/15">
          {sections.map((section, index) => {
            const isExpanded = expandedItems.has(index);

            return (
              <div
                key={`section-${index}`}
                className="border-b border-white/15"
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="group flex w-full cursor-pointer items-center justify-between py-4 text-left"
                  aria-expanded={isExpanded}
                  aria-controls={`pc-accordion-${platform.id}-${index}`}
                >
                  <span className="text-base font-medium text-white">
                    {section.title}
                  </span>
                  {isExpanded ? (
                    <X className="h-4 w-4 shrink-0 text-white/60" />
                  ) : (
                    <Plus className="h-4 w-4 shrink-0 text-white/60 transition-transform duration-300 group-hover:rotate-90" />
                  )}
                </button>

                <div
                  id={`pc-accordion-${platform.id}-${index}`}
                  className={cn(
                    "overflow-hidden transition-all duration-300",
                    isExpanded ? "max-h-[500px]" : "max-h-0"
                  )}
                >
                  <div className="pb-4">
                    {section.excerpt && (
                      <p className="text-sm lg:text-base 2xl:text-lg text-white/70">
                        {section.excerpt}
                      </p>
                    )}
                    <Link
                      href={`/platforms/${platform.slug}`}
                      className="group mt-4 inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-sm text-white/80 transition-colors hover:border-white hover:text-white"
                    >
                      Read More
                      <span className="inline-block text-lg leading-none transition-transform duration-300 group-hover:rotate-90">
                        +
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
